import { useEffect, useState } from 'react';
import api from '../api/api';
import styled from 'styled-components';

const Container = styled.section`
  padding: 4rem 2rem;
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  @media (min-width: 1024px) {
    padding: 6rem 10% 4rem;
  }
`;

const ContactLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: start;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1.2fr;
  }
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: #f8fafc;
  line-height: 1.2;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SectionSubtitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #818cf8;
  margin-bottom: 1rem;
`;

const SectionContent = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: #94a3b8;
  margin-bottom: 1.5rem;
`;

const ContactImage = styled.img`
  width: 100%;
  max-height: 280px;
  object-fit: cover;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
`;

const FormColumn = styled.div`
  background: rgba(30, 41, 59, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(10px);
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #e2e8f0;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.85rem 1rem;
  background: rgba(15, 23, 42, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: #f8fafc;
  font-size: 0.95rem;
  transition: all 0.3s ease;

  &::placeholder {
    color: #475569;
  }

  &:focus {
    outline: none;
    border-color: #6366f1;
    background: rgba(15, 23, 42, 0.6);
    box-shadow: 0 0 12px rgba(99, 102, 241, 0.3);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.85rem 1rem;
  background: rgba(15, 23, 42, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: #f8fafc;
  font-size: 0.95rem;
  min-height: 140px;
  resize: vertical;
  transition: all 0.3s ease;

  &::placeholder {
    color: #475569;
  }

  &:focus {
    outline: none;
    border-color: #6366f1;
    background: rgba(15, 23, 42, 0.6);
    box-shadow: 0 0 12px rgba(99, 102, 241, 0.3);
  }
`;

const SubmitButton = styled.button`
  padding: 0.95rem 1.5rem;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  margin-top: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(99, 102, 241, 0.5);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Empty = styled.p`
  text-align: center;
  color: #64748b;
  font-size: 1.1rem;
  padding: 4rem 2rem;
  background: rgba(30, 41, 59, 0.2);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.03);
`;

function Contato() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  useEffect(() => {
    fetchSectionContent();
  }, []);

  const fetchSectionContent = async () => {
    setLoading(true);
    try {
      const response = await api.get('/sections/contato');
      setContent(response.data.data || response.data);
    } catch (err) {
      console.error('Erro ao carregar seção contato:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Formulário enviado:', formData);
    setFormData({ name: '', email: '', message: '' });
  };

  if (loading) {
    return (
      <Container>
        <Empty>Carregando...</Empty>
      </Container>
    );
  }

  // Se der erro ou não vier conteúdo, usamos dados de fallback para manter a página utilizável
  const title = content?.title || 'Fale Comigo';
  const subtitle = content?.subtitle || 'Entre em contato para novas oportunidades, dúvidas ou feedbacks.';
  const description = content?.description || 'Preencha o formulário ao lado com suas informações e mensagem. Responderei o mais rápido possível!';
  const imageUrl = content?.imageUrl || '';
  const buttonText = content?.buttonText || 'Enviar mensagem';

  return (
    <Container id="contato">
      
      <ContactLayout>
        <InfoColumn>
          <SectionTitle>{title}</SectionTitle>
          {subtitle && <SectionSubtitle>{subtitle}</SectionSubtitle>}
          {imageUrl && <ContactImage src={imageUrl} alt="Contato" />}
          {description && <SectionContent>{description}</SectionContent>}
        </InfoColumn>

        <FormColumn>
          <ContactForm onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="name">Seu Nome</Label>
              <Input
                id="name"
                type="text"
                name="name"
                placeholder="Ex: Maria Souza"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">Seu E-mail</Label>
              <Input
                id="email"
                type="email"
                name="email"
                placeholder="Ex: maria@example.com"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">Sua Mensagem</Label>
              <Textarea
                id="message"
                name="message"
                placeholder="Escreva sua mensagem aqui..."
                value={formData.message}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <SubmitButton type="submit">
              {buttonText}
            </SubmitButton>
          </ContactForm>
        </FormColumn>
      </ContactLayout>
    </Container>
  );
}

export default Contato;
